import {useEffect, useMemo, useState} from "react";
import {Link, useSearchParams} from "react-router-dom";
import {createJobApplication, getJobVacancies} from "../../api/client";

const fallbackVacancies = [
    {id: "automotive-mechanic", title: "Автомеханик"},
    {id: "auto-locksmith", title: "Автослесарь"},
    {id: "diagnostician", title: "Диагност"},
    {id: "auto-electrician", title: "Автоэлектрик"},
    {id: "truck-repair-specialist", title: "Специалист по ремонту грузовой техники"},
    {id: "assistant-master", title: "Помощник мастера"},
    {id: "request-manager", title: "Администратор / менеджер по заявкам"},
];
const contactOptions = [
    {id: "phone", title: "Телефон"},
    {id: "telegram", title: "Telegram"},
    {id: "whatsapp", title: "WhatsApp"},
    {id: "max", title: "MAX"},
];

function isBrokenVacancyText(value) {
    if (!value) {
        return true;
    }

    const questionMarksCount = (value.match(/\?/g) || []).length;
    return questionMarksCount >= Math.max(3, Math.floor(value.length * 0.35));
}

function getAccountDetailsStorageKey(user) {
    if (!user) {
        return null;
    }

    return `truckcenter-account-details-${user.id || user.phone}`;
}

function readAccountDetails(user) {
    const storageKey = getAccountDetailsStorageKey(user);

    if (!storageKey) {
        return null;
    }

    try {
        const storedDetails = window.localStorage.getItem(storageKey);
        return storedDetails ? JSON.parse(storedDetails) : null;
    } catch {
        return null;
    }
}

function StaffApplyDropdown({label, value, placeholder, options, isOpen, onToggle, onClose, onSelect}) {
    const selectedOption = options.find((option) => option.title === value);

    return (
        <div className="staff-apply-field">
            <span>{label}</span>
            <div
                className="staff-apply-dropdown"
                data-open={isOpen}
                onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        onClose();
                    }
                }}
            >
                <button
                    className="staff-apply-dropdown-toggle"
                    type="button"
                    aria-expanded={isOpen}
                    aria-haspopup="listbox"
                    onClick={onToggle}
                >
                    <span key={value}>{selectedOption?.title || placeholder}</span>
                    <span className="staff-apply-dropdown-arrow" aria-hidden="true"/>
                </button>
                <div className="staff-apply-dropdown-list" role="listbox" aria-label={label}>
                    {options.map((option) => (
                        <button
                            className="staff-apply-dropdown-option"
                            data-selected={option.title === value}
                            key={option.id}
                            type="button"
                            role="option"
                            aria-selected={option.title === value}
                            onClick={() => {
                                onSelect(option.title);
                                onClose();
                            }}
                        >
                            {option.title}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function StaffApply({auth}) {
    const [searchParams] = useSearchParams();
    const user = auth?.user;
    const selectedPosition = searchParams.get("position") || "";
    const accountDetails = useMemo(() => readAccountDetails(user), [user]);
    const autofillName = accountDetails?.fullName || user?.fullName || "";
    const autofillPhone = accountDetails?.phone || user?.phone || "";
    const [vacancies, setVacancies] = useState([]);
    const [position, setPosition] = useState(selectedPosition);
    const [preferredContact, setPreferredContact] = useState("Телефон");
    const [openDropdown, setOpenDropdown] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState("");
    const [submitError, setSubmitError] = useState("");

    useEffect(() => {
        let isMounted = true;

        getJobVacancies()
            .then((items) => {
                if (isMounted) {
                    setVacancies(items);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setVacancies([]);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        setPosition(selectedPosition);
    }, [selectedPosition]);

    const positionOptions = useMemo(() => {
        const hasReadableVacancies = vacancies.some((vacancy) => !isBrokenVacancyText(vacancy.title));
        const vacancyOptions = hasReadableVacancies ? vacancies : fallbackVacancies;
        const hasSelectedPosition = selectedPosition && vacancyOptions.some((vacancy) => vacancy.title === selectedPosition);

        return hasSelectedPosition || !selectedPosition
            ? vacancyOptions
            : [{id: "selected-position", title: selectedPosition}, ...vacancyOptions];
    }, [selectedPosition, vacancies]);

    async function handleSubmit(event) {
        event.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus("");
        setSubmitError("");
        const form = event.currentTarget;

        if (!position) {
            setSubmitError("Выберите желаемую должность");
            setIsSubmitting(false);
            return;
        }

        const formData = new FormData(event.currentTarget);

        try {
            const response = await createJobApplication({
                fullName: formData.get("fullName"),
                phone: formData.get("phone"),
                position,
                experience: formData.get("experience"),
                preferredContact,
                message: formData.get("message"),
            });

            form.reset();
            setPosition(selectedPosition);
            setPreferredContact("Телефон");
            setSubmitStatus(response.message || "Отклик отправлен. Мы свяжемся с вами.");
        } catch (requestError) {
            setSubmitError(requestError.message);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <main className="staff-page staff-apply-page">
            <section className="staff-hero staff-apply-hero">
                <div className="staff-hero-content">
                    <span className="staff-eyebrow">Отклик на вакансию</span>
                    <h1>Заполнить заявку кандидата</h1>
                    <p className="staff-hero-description">
                        Укажите желаемую должность, опыт и контакты. Регистрация не нужна — отклик уйдет напрямую в
                        компанию.
                    </p>
                </div>

                <div className="staff-hero-actions">
                    <Link className="staff-home-link" to="/staff#careers">
                        К вакансиям
                    </Link>
                </div>
            </section>

            <section className="staff-apply-layout">
                <form className="staff-apply-card" onSubmit={handleSubmit}>
                    <div className="staff-careers-heading">
                        <span className="staff-eyebrow">Данные кандидата</span>
                        <h2>Откликнуться</h2>
                    </div>

                    {submitStatus && <p className="staff-status staff-status-success">{submitStatus}</p>}
                    {submitError && <p className="staff-status staff-status-error">{submitError}</p>}

                    <label>
                        Имя
                        <input
                            name="fullName"
                            autoComplete="name"
                            defaultValue={autofillName}
                            maxLength="120"
                            placeholder="Как к вам обращаться"
                            required
                        />
                    </label>
                    <label>
                        Телефон
                        <input
                            name="phone"
                            type="tel"
                            autoComplete="tel"
                            inputMode="tel"
                            defaultValue={autofillPhone}
                            pattern="^\+?[0-9\s()\-]{10,20}$"
                            maxLength="20"
                            placeholder="+7 950 000 00 00"
                            required
                        />
                    </label>
                    <StaffApplyDropdown
                        label="Желаемая должность"
                        value={position}
                        placeholder="Выберите профессию"
                        options={positionOptions}
                        isOpen={openDropdown === "position"}
                        onToggle={() => setOpenDropdown((currentDropdown) => currentDropdown === "position" ? "" : "position")}
                        onClose={() => setOpenDropdown("")}
                        onSelect={setPosition}
                    />
                    <StaffApplyDropdown
                        label="Удобный способ связи"
                        value={preferredContact}
                        placeholder="Выберите способ связи"
                        options={contactOptions}
                        isOpen={openDropdown === "contact"}
                        onToggle={() => setOpenDropdown((currentDropdown) => currentDropdown === "contact" ? "" : "contact")}
                        onClose={() => setOpenDropdown("")}
                        onSelect={setPreferredContact}
                    />
                    <label>
                        Опыт работы
                        <textarea name="experience" maxLength="2000" placeholder="Кратко: где работали, с какой техникой, какие задачи выполняли"/>
                    </label>
                    <label>
                        Комментарий
                        <textarea name="message" maxLength="2000" placeholder="Когда удобно связаться, какие условия хотите обсудить"/>
                    </label>

                    <button className="staff-apply-submit" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? "Отправляем..." : "Отправить отклик"}
                    </button>
                </form>

                <aside className="staff-apply-note">
                    <span className="staff-eyebrow">Что указать</span>
                    <h2>Лучше заполнить кратко и по делу</h2>
                    <ul>
                        <li>имя и номер телефона;</li>
                        <li>желаемую должность;</li>
                        <li>опыт работы с техникой;</li>
                        <li>удобный способ связи;</li>
                        <li>когда можно связаться.</li>
                    </ul>
                </aside>
            </section>
        </main>
    );
}

export default StaffApply;
